
import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { cn } from '@/lib/utils';
import { useEditorConfig } from './editor/useEditorConfig';
import { useEditorShortcuts } from './editor/useEditorShortcuts';
import { useEditorStyles } from './editor/useEditorStyles';

// Import our extracted components
import EditorToolbar from './editor/EditorToolbar';
import EditorBubbleMenu from './editor/EditorBubbleMenu';
import EditorFooter from './editor/EditorFooter';
import './editor/extensions/editor-extensions.css';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  uploadImage?: (file: File) => Promise<string>;
  className?: string;
}

const RichTextEditor = ({ 
  content, 
  onChange, 
  placeholder, 
  uploadImage,
  className
}: RichTextEditorProps) => {
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState(content);
  
  // Use our custom hook to get editor configuration
  const editorConfig = useEditorConfig(placeholder);
  
  const editor = useEditor({
    extensions: editorConfig.extensions,
    content,
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML();
      onChange(newContent);
      
      // We'll implement autosave functionality here later
      // For now, just update the last saved content
      setLastSavedContent(newContent);
    },
    // Add parsing settings to preserve HTML
    parseOptions: {
      preserveWhitespace: 'full',
    },
    // Enable easier debugging
    onTransaction: ({ editor, transaction }) => {
      // Useful for logging complex transactions
      if (transaction.steps.length > 2) {
        console.log("Complex transaction detected:", transaction);
      }
    },
    editorProps: {
      // This enables direct pasting of HTML content
      handlePaste: (view, event) => {
        if (event.clipboardData && event.clipboardData.getData('text/html')) {
          // Log for debugging
          console.log("HTML content pasted:", event.clipboardData.getData('text/html'));
        }
        return false; // Let TipTap handle the paste normally
      }
    }
  });
  
  useEffect(() => {
    // Update editor content when prop changes (e.g. when loading an existing article)
    if (editor && content !== lastSavedContent) {
      editor.commands.setContent(content);
      setLastSavedContent(content);
    }
  }, [content, editor, lastSavedContent]);
  
  // Use our keyboard shortcut handler
  const handleKeyDown = useEditorShortcuts(editor);
  
  // Add custom styles for video embeds and galleries
  useEditorStyles();
  
  if (!editor) {
    return null;
  }
  
  return (
    <div className={cn("border rounded-md", className)} onKeyDown={handleKeyDown}>
      <EditorToolbar 
        editor={editor}
        uploadImage={uploadImage}
      />
      
      {/* Bubble menu for quick formatting */}
      <EditorBubbleMenu 
        editor={editor}
        setLinkPopoverOpen={setLinkPopoverOpen}
      />
      
      <EditorContent 
        editor={editor} 
        className="prose prose-sm sm:prose-base max-w-none p-4 min-h-[200px]" 
      />
      
      <EditorFooter editor={editor} />
    </div>
  );
};

export default RichTextEditor;
